import type { FieldCatalogueEntry, FieldValue, RequestFieldView } from "@procuremate/shared";
import type { TFunction } from "i18next";
import type { Language } from "@/i18n";

/**
 * A stored value as the requester reads it: localised yes/no and choice labels, list items
 * kept apart, "not applicable" / "not provided" spelled out. Text stays exactly as stored.
 */
export function displayValue(
  field: Pick<RequestFieldView, "value" | "status">,
  entry: FieldCatalogueEntry | undefined,
  language: Language,
  t: TFunction,
): string | string[] {
  if (field.status === "NOT_APPLICABLE") return t("requests.panel.notApplicable");
  if (field.status === "NOT_PROVIDED") return t("requests.panel.notProvided");
  const value = field.value;
  if (typeof value === "boolean") return value ? t("requests.panel.yes") : t("requests.panel.no");
  if (Array.isArray(value)) return value;
  if (value === null) return "";
  if (entry?.type === "choice") return entry.options.find((o) => o.value === value)?.labels[language] ?? value;
  return value;
}

/** The editor's starting text for a value (lists: one item per line). */
export function editorText(value: FieldValue | undefined): string {
  if (Array.isArray(value)) return value.join("\n");
  if (typeof value === "string") return value;
  return "";
}

/** The editor's text as the value to send, or null when nothing was entered. */
export function valueToSend(type: FieldCatalogueEntry["type"], text: string): string | string[] | null {
  if (type === "list") {
    const items = text
      .split("\n")
      .map((x) => x.trim())
      .filter(Boolean);
    return items.length > 0 ? items : null;
  }
  const trimmed = text.trim();
  return trimmed ? trimmed : null;
}
